// Time-bucketing helpers used by both the Overview chart and the
// Overview print report. Each `bucketBy*` returns an ordered array of
// { label, date, count } so the consumer just iterates.

import type { Suggestion } from "../types/ess";

export interface TrendBucket {
  label: string;
  date: Date;
  count: number;
}

export type TrendGranularity = "day" | "week" | "month" | "quarter";

export function pickTrendGranularity(days: number): TrendGranularity {
  if (days <= 45) return "day";
  if (days <= 365) return "week";
  if (days <= 365 * 5) return "month";
  return "quarter";
}

export function bucketByDay(
  rows: Suggestion[],
  from: Date,
  to: Date,
): TrendBucket[] {
  const days: TrendBucket[] = [];
  const start = new Date(from);
  start.setHours(0, 0, 0, 0);
  const end = new Date(to);
  end.setHours(0, 0, 0, 0);
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    days.push({
      label: d.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      }),
      date: new Date(d),
      count: 0,
    });
  }
  const indexFor = (dt: Date) =>
    Math.floor((dt.getTime() - start.getTime()) / 86400000);
  for (const r of rows) {
    const dt = new Date(r.created_at);
    const idx = indexFor(dt);
    if (idx >= 0 && idx < days.length) days[idx].count += 1;
  }
  return days;
}

export function bucketByWeek(
  rows: Suggestion[],
  from: Date,
  to: Date,
): TrendBucket[] {
  const weeks: TrendBucket[] = [];
  const startOfWeek = (d: Date) => {
    const out = new Date(d);
    out.setHours(0, 0, 0, 0);
    out.setDate(out.getDate() - out.getDay());
    return out;
  };
  let cursor = startOfWeek(from);
  const end = startOfWeek(to);
  while (cursor <= end) {
    weeks.push({
      label: cursor.toLocaleDateString(undefined, {
        month: "short",
        day: "numeric",
      }),
      date: new Date(cursor),
      count: 0,
    });
    cursor.setDate(cursor.getDate() + 7);
  }
  const startMs = weeks[0]?.date.getTime() ?? 0;
  for (const r of rows) {
    const dt = startOfWeek(new Date(r.created_at));
    const idx = Math.floor((dt.getTime() - startMs) / (86400000 * 7));
    if (idx >= 0 && idx < weeks.length) weeks[idx].count += 1;
  }
  return weeks;
}

export function bucketByMonth(
  rows: Suggestion[],
  from: Date,
  to: Date,
): TrendBucket[] {
  const months: TrendBucket[] = [];
  const cursor = new Date(from.getFullYear(), from.getMonth(), 1);
  const end = new Date(to.getFullYear(), to.getMonth(), 1);
  while (cursor <= end) {
    months.push({
      label: cursor.toLocaleDateString(undefined, {
        month: "short",
        year: "2-digit",
      }),
      date: new Date(cursor),
      count: 0,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  const keyOf = (dt: Date) => `${dt.getFullYear()}-${dt.getMonth()}`;
  const indexByKey = new Map(
    months.map((b, i) => [keyOf(b.date), i] as const),
  );
  for (const r of rows) {
    const dt = new Date(r.created_at);
    const idx = indexByKey.get(keyOf(dt));
    if (idx !== undefined) months[idx].count += 1;
  }
  return months;
}

export function bucketByQuarter(
  rows: Suggestion[],
  from: Date,
  to: Date,
): TrendBucket[] {
  const quarters: TrendBucket[] = [];
  const startQ = Math.floor(from.getMonth() / 3);
  const cursor = new Date(from.getFullYear(), startQ * 3, 1);
  const endQ = Math.floor(to.getMonth() / 3);
  const end = new Date(to.getFullYear(), endQ * 3, 1);
  while (cursor <= end) {
    const q = Math.floor(cursor.getMonth() / 3) + 1;
    quarters.push({
      label: `Q${q} ${String(cursor.getFullYear()).slice(2)}`,
      date: new Date(cursor),
      count: 0,
    });
    cursor.setMonth(cursor.getMonth() + 3);
  }
  const keyOf = (dt: Date) =>
    `${dt.getFullYear()}-${Math.floor(dt.getMonth() / 3)}`;
  const indexByKey = new Map(
    quarters.map((b, i) => [keyOf(b.date), i] as const),
  );
  for (const r of rows) {
    const dt = new Date(r.created_at);
    const idx = indexByKey.get(keyOf(dt));
    if (idx !== undefined) quarters[idx].count += 1;
  }
  return quarters;
}

export function bucketRange(
  rows: Suggestion[],
  from: Date,
  to: Date,
  granularity: TrendGranularity,
): TrendBucket[] {
  switch (granularity) {
    case "day":
      return bucketByDay(rows, from, to);
    case "week":
      return bucketByWeek(rows, from, to);
    case "month":
      return bucketByMonth(rows, from, to);
    case "quarter":
      return bucketByQuarter(rows, from, to);
  }
}
